import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { businessActions, businessSelectors, businessTypes } from 'app-components/flux/business';
import { profileSelectors } from 'app-components/flux/profile';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import { useFetchOnce } from 'app-components/hooks/utils';
import { tracker } from 'app-components/utils/core';
import BusinessColorSelect from 'components/Business/BusinessColorSelect';
import BusinessLogoField from 'components/Business/BusinessLogoField';
import InfoTooltipIcon from 'components/InfoTooltipIcon';
import QIllustration from 'components/QIllustration';

import {
  DIALOG_TYPE as CONFIRMATION_DIALOG_TYPE,
  mkConfirmationDialogProps,
} from 'components/Dialogs/ConfirmationDialog';
import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogContent from 'components/Dialogs/StdDialogContent';
import Dump from 'components/Dump';
import QButton from 'components/QButton';
import QChoiceTextField from 'components/QuickenControls/QChoiceChips/QChoiceTextField';
import ZeroStateView from 'components/ZeroStateView';
import BusinessCard from 'containers/BusinessCard';
import useLazyLoadReducer from 'hooks/useLazyLoadReducer';
import { Map as ImmutableMap } from 'immutable';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTimeoutFn } from 'react-use';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid } from 'uuid';

function reducer(state, action) {
  if (action.payload?.length > 225) {
    return state;
  }
  switch (action.type) {
    case 'firstName':
      return { ...state, firstName: action.payload };
    case 'lastName':
      return { ...state, lastName: action.payload };
    case 'zipCode':
      return { ...state, zipCode: action.payload };
    case 'screen':
      return { ...state, screen: action.payload };
    case 'hideBackdrop':
      return { ...state, hideBackdrop: action.payload };
    case 'accountingType':
      return { ...state, accountingType: action.payload };
    case 'industry':
      return { ...state, industry: action.payload };
    case 'businessName':
      return { ...state, businessName: action.payload };
    case 'color':
      return { ...state, color: action.payload };
    case 'structure':
      return {
        ...state,
        structure: action.payload,
        hideBackdrop: action.payload === 'C_CORP',
      };
    case 'clear-business':
      return { ...state, industry: null, structure: null, businessName: null, color: null };
    case 'line1':
      return { ...state, line1: action.payload };
    case 'line2':
      return { ...state, line2: action.payload };
    case 'state':
      return { ...state, state: action.payload };
    case 'city':
      return { ...state, city: action.payload };
    case 'hasGoneForward':
      return { ...state, hasGoneForward: true };
    case 'hasGoneForwardReset':
      return { ...state, hasGoneForward: false };
    case 'add-another-business':
      return {
        ...state,
        hasGoneForward: false,
        industry: null,
        structure: null,
        businessName: '',
        color: null,
        email: '',
        logoId: null,
        screen: 'businessEntry',
      };
    case 'taxGroup':
      return { ...state, taxGroup: action.payload };
    case 'email':
      return { ...state, email: action.payload };
    case 'phoneNumber':
      return { ...state, phoneNumber: action.payload };
    case 'logoId':
      return { ...state, logoId: action.payload };
    default:
      return state;
  }
}

export const useStyles = makeStyles()((theme) => ({
  contentSpace: {
    height: 386,
    width: 'calc(100% - 48px)',
    position: 'relative',
    margin: '16px',
  },
  nameRow: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    width: '100%',
  },
  thumbsUp: {
    display: 'flex',
    alignItems: 'flex-start',
    height: 105,
    paddingRight: 16,
    width: '100%',
    gap: 16,
  },
  businessField: {
    width: '100%',
  },
  infoIcon: {
    color: theme.palette.icon.light,
    marginLeft: 8,
    height: 24,
    width: 24,
  },
  addressField: {
    width: '50%',
    paddingRight: 16,
  },
  stateField: {
    width: '20%',
    paddingRight: 16,
  },
  zipField: {
    width: '30%',
  },
  businessStructure: {
    width: '60%',
    paddingRight: 8,
  },
  businessCardGSF: {
    padding: '16px 20px',
    margin: '6px 2px',
    marginBottom: '6px',
    borderRadius: 8,
  },
  addAnotherBusiness: {
    marginTop: 24,
    marginBottom: 12,
  },
  successScreenBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '80%',
    width: '100%',
    textAlign: 'center',
    flexGrow: 1,
    minHeight: '300px',
  },
}));

const accountingArr = [
  { label: 'Cash basis', value: 'CASH' },
  { label: 'Accrual basis', value: 'ACCRUAL' },
];
const tooltip = (
  <span>
    <b>Cash basis (default)</b>
    <br />
    Records revenue when cash is received and expenses when cash is paid out.
    <br />
    <b>Accrual basis</b>
    <br />
    Records revenue and expenses only when services have been rendered, even if unpaid. Note: You can override this
    later in Reports
  </span>
);

const isBlank = (str) => str?.trim() === '' && str !== '';

const RenderBusinessEntry = (props) => {
  const { updateLocalState, state, business, isGSF } = props;
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const colorArr = useSelector(businessSelectors.getBusinessColors);
  if (!colorArr.find((c) => c === state.color) && state.color !== '' && state.color !== null) {
    colorArr.push(state.color);
  }
  const businessNames = useSelector(businessSelectors.getBusinessNames);
  const showCopy = Object.keys(businessNames).length === 0;
  const profile = useSelector(profileSelectors.getProfile);
  const names = profile?.fullName?.split(' ');
  const firstName = names?.[0] || profile?.firstName;
  const businessName = state.businessName || '';
  const duplicateError =
    businessName.trim() in businessNames && (businessName.trim() !== (business?.name ?? '').trim() || !business);
  const nameEmptyError = isBlank(businessName ?? '');
  const invalidNameEntry = duplicateError || nameEmptyError;
  const emailEmptyError = isBlank(state.email ?? '');
  let businessNameHelperText = nameEmptyError ? 'Business name is required' : null;
  businessNameHelperText = duplicateError ? 'Already tracking a business with this name' : null;

  return (
    <div className={classes.thumbsUp}>
      <QIllustration name='businessBriefcase' alt='business-briefcase' size='md' />
      <div className={classes.nameRow}>
        {showCopy && isGSF ? (
          <>
            <Typography variant='titleMedium'>{`Hey ${firstName}!`}</Typography>
            <Typography variant='bodyRegular'>
              Enter your business information. If you have more than one business, you’ll be able to add another one on
              the next screen.
            </Typography>
          </>
        ) : (
          <Typography variant='bodyRegular'>Enter your business information below.</Typography>
        )}
        <Stack flexDirection='row' alignItems='center' pr={4} gap={2}>
          <TextField
            id='business_name_field'
            label='Business name'
            className={classes.businessField}
            margin='normal'
            variant='filled'
            defaultValue={businessName}
            onChange={(e) => {
              updateLocalState('businessName', e.target.value);
            }}
            helperText={businessNameHelperText}
            error={invalidNameEntry}
          />
          <TextField
            id='industry-select-element'
            label='Industry'
            select
            className={classes.businessField}
            margin='normal'
            variant='filled'
            defaultValue={state.industry || ''}
            onChange={(e) => {
              updateLocalState('industry', e.target.value);
            }}
          >
            {Object.keys(businessTypes.industryObj).map((key) => (
              <MenuItem key={key} value={key}>
                {businessTypes.industryObj[key]}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <Box display='flex' alignItems='center'>
          <TextField
            id='business-structure-select-element'
            label='Business structure'
            select
            className={classes.businessStructure}
            margin='normal'
            variant='filled'
            defaultValue={state.structure || ''}
            onChange={(e) => {
              updateLocalState('structure', e.target.value);
              if (e.target.value === 'C_CORP') {
                dispatch(
                  rootUiActions.createDialog(
                    rootUiTypes.mkRootUiData({
                      id: 'unsuported-business',
                      type: CONFIRMATION_DIALOG_TYPE,
                      allowNesting: true,
                      props: ImmutableMap(
                        mkConfirmationDialogProps({
                          title: 'Unsupported business structure',
                          content: (
                            <span>
                              Simplifi does not yet support <b>C Corp</b> businesses. <br /> <br />
                              You may still add this business, but you will need to create and associate categories to
                              the appropriate tax lines in order to create tax reports.
                            </span>
                          ),
                          confirmLabel: 'Confirm',
                          hideBackdrop: !isGSF,
                          denyLabel: null,
                          confirmAction: () => {
                            updateLocalState('hideBackdrop', false);
                          },
                          denyAction: () => {
                            updateLocalState('hideBackdrop', false);
                          },
                        }),
                      ),
                    }),
                  ),
                );
              }
            }}
          >
            {Object.keys(businessTypes.businessStructureObj).map((key) => (
              <MenuItem key={key} value={key}>
                {businessTypes.businessStructureObj[key]}
              </MenuItem>
            ))}
          </TextField>
          <BusinessColorSelect
            currColor={state.color || ''}
            onChange={(e) => {
              updateLocalState('color', e.target.value);
            }}
          />
        </Box>
        <Box display='flex' alignItems='center' mt={1}>
          <QChoiceTextField
            text='Accounting method'
            QChoiceChipsProps={{
              items: accountingArr,
              labelFromItem: (item) => item.label,
              keyFromItem: (item) => item.value,
              selectedItems: [state.accountingType === 'CASH' ? accountingArr[0] : accountingArr[1]],
              onSelected: (val) => {
                updateLocalState('accountingType', val[0].value);
              },
            }}
          />
          <InfoTooltipIcon tooltipHTML={tooltip} size='medium' className={classes.infoIcon} />
        </Box>
        <TextField
          id='email_field'
          label='Email'
          className={classes.businessField}
          margin='normal'
          variant='filled'
          defaultValue={state.email || ''}
          onChange={(e) => {
            updateLocalState('email', e.target.value);
          }}
          sx={{
            pr: 4,
            mb: 2,
          }}
          error={emailEmptyError}
          helperText={emailEmptyError ? 'Email is required' : null}
        />
        <BusinessLogoField
          initialLogoId={state.logoId || ''}
          onSelect={(logoId) => updateLocalState('logoId', logoId)}
        />
      </div>
    </div>
  );
};

RenderBusinessEntry.propTypes = {
  updateLocalState: PropTypes.func,
  state: PropTypes.object,
  business: PropTypes.object,
  isGSF: PropTypes.bool,
};

const RenderAddressEntry = (props) => {
  const { updateLocalState, state } = props;
  const { classes } = useStyles();
  return (
    <div className={classes.thumbsUp}>
      <QIllustration name='businessBriefcase' alt='business-briefcase' size='md' />
      <div className={classes.nameRow}>
        <Typography variant='bodyRegular'>Contact info (optional) </Typography>
        <Box>
          <TextField
            id='phone_field'
            label='Phone number'
            className={classes.addressField}
            margin='normal'
            variant='filled'
            onChange={(e) => {
              updateLocalState('phoneNumber', e.target.value);
            }}
            sx={{
              pr: 4,
            }}
          />
        </Box>
        <Typography variant='bodyRegular'>Enter your business address (optional) </Typography>
        <TextField
          id='street_address_field'
          label='Street address'
          className={classes.addressField}
          margin='normal'
          variant='filled'
          onChange={(e) => {
            updateLocalState('line1', e.target.value);
          }}
          sx={{
            pr: 4,
          }}
        />
        <TextField
          id='street_address_line2_field'
          label='Address line 2'
          className={classes.addressField}
          margin='normal'
          variant='filled'
          onChange={(e) => {
            updateLocalState('line2', e.target.value);
          }}
          sx={{
            pr: 4,
          }}
        />
        <Box>
          <TextField
            id='city_field'
            label='City'
            className={classes.addressField}
            margin='normal'
            variant='filled'
            onChange={(e) => {
              updateLocalState('city', e.target.value);
            }}
            sx={{
              pr: 4,
            }}
          />
          <TextField
            id='state_field'
            label='State'
            className={classes.stateField}
            margin='normal'
            variant='filled'
            defaultValue={state.state}
            onChange={(e) => {
              updateLocalState('state', e.target.value);
            }}
            select
          >
            {businessTypes.usStatesArray.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            id='zip_field'
            label='ZIP Code'
            className={classes.zipField}
            margin='normal'
            variant='filled'
            defaultValue={state.zipCode}
            onChange={(e) => {
              updateLocalState('zipCode', e.target.value);
            }}
          />
        </Box>
      </div>
    </div>
  );
};

RenderAddressEntry.propTypes = {
  updateLocalState: PropTypes.func,
  state: PropTypes.object,
};

const RenderTaxFormCheck = (props) => {
  const { updateLocalState, business } = props;
  const { classes } = useStyles();
  return (
    <div className={classes.thumbsUp}>
      <QIllustration name='businessBriefcase' alt='business-briefcase' size='md' />
      {business?.taxGroup && (
        <div className={classes.nameRow}>
          <Typography variant='bodyRegular'>
            <span>
              Based on your business details, the most common form filed annually with the IRS is{' '}
              <b>{businessTypes.taxGroupNames[business?.taxGroup]}</b>.
            </span>
          </Typography>
          <Typography variant='bodyRegular' sx={{ py: 2 }}>
            You can change the tax form below, or later on in <b>Settings</b>.
          </Typography>
          <TextField
            id='tax-group-select'
            label='Tax Group'
            select
            defaultValue={business?.taxGroup || ''}
            onChange={(e) => {
              updateLocalState('taxGroup', e.target.value);
              tracker.track(tracker.events.updatedTaxGroup, {
                inferred_tax_group: business?.taxGroup,
                tax_group: e.target.value,
              });
            }}
          >
            {Object.keys(businessTypes.taxGroupNames).map((key) => (
              <MenuItem key={key} value={key}>
                {businessTypes.taxGroupCategoryNames[key]} (i.e. {businessTypes.taxGroupNames[key]})
              </MenuItem>
            ))}
          </TextField>
        </div>
      )}
    </div>
  );
};

RenderTaxFormCheck.propTypes = {
  updateLocalState: PropTypes.func,
  business: PropTypes.object,
};

const SuccessScreen = (props) => {
  const { onClose } = props;
  const { classes } = useStyles();

  useTimeoutFn(onClose, 1500);
  tracker.track(tracker.events.addBusinessComplete);

  return (
    <Box className={classes.successScreenBox}>
      <ZeroStateView description='Business successfully added!' qIllustration='thumbsUp' />
    </Box>
  );
};
SuccessScreen.propTypes = {
  onClose: PropTypes.func,
};

const RenderBusinessList = (props) => {
  const { updateLocalState, updateTitle, isGSF } = props;
  const { classes } = useStyles();
  const businesses = useSelector(businessSelectors.getResourcesById);

  useEffect(() => {
    if (isGSF) {
      updateTitle('Saved businesses');
    }
  }, [isGSF, updateTitle]);

  return (
    <div className={classes.thumbsUp}>
      <QIllustration name='businessBriefcase' alt='business-briefcase' size='md' />
      <div className={classes.nameRowBusiness}>
        <Typography variant='bodyRegular' sx={{ paddingBottom: 3 }}>
          Enter your business information. If you have more than one business, you’ll be able to add another one on the
          next screen.
        </Typography>
        <Box display='flex' flexDirection='column' alignItems='flex-start' width='100%'>
          {businesses.toList().map((business) => (
            <BusinessCard key={business.name} business={business} className={classes.businessCardGSF} hideMenu isGSF />
          ))}
          <QButton
            onClick={() => {
              updateLocalState('add-another-business');
              if (isGSF) {
                updateTitle('Tell us about your business');
              }
            }}
            className={classes.addAnotherBusiness}
          >
            + Add another business
          </QButton>
        </Box>
      </div>
    </div>
  );
};

RenderBusinessList.propTypes = {
  updateLocalState: PropTypes.func,
  updateTitle: PropTypes.func,
  isGSF: PropTypes.bool,
};

const configSettings = {
  businessEntry: {
    prev: null,
    next: 'addressEntry',
    component: RenderBusinessEntry,
    buttonText: 'Continue',
    action: 'create',
  },
  addressEntry: {
    prev: 'businessEntry',
    next: 'taxFormCheck',
    component: RenderAddressEntry,
    buttonText: 'Continue',
    secondaryButtonText: 'Create',
    skip: 'successScreen',
    action: 'update',
  },
  taxFormCheck: {
    prev: 'addressEntry',
    next: 'successScreen',
    component: RenderTaxFormCheck,
    buttonText: 'Create',
    action: 'update',
  },
  successScreen: {
    prev: 'taxFormCheck',
    next: null,
    component: SuccessScreen,
    buttonText: null,
    action: null,
  },
};

const configGSF = {
  businessEntry: {
    title: 'Tell us about your business',
    prev: null,
    next: 'taxFormCheck',
    skip: 'businessList',
    component: RenderBusinessEntry,
    buttonText: 'Continue',
    action: 'create',
  },
  taxFormCheck: {
    title: 'Confirm your tax form',
    prev: 'businessEntry',
    next: 'businessList',
    component: RenderTaxFormCheck,
    buttonText: 'Continue',
    action: 'update',
  },
  businessList: {
    title: 'Saved businesses',
    component: RenderBusinessList,
    buttonText: 'Next',
    next: 'exit',
    prev: 'businessEntry',
    isExit: true,
  },
};

const Content = (props) => {
  const {
    goForward,
    state,
    updateLocalState,
    isGSF,
    setCurrentBusinessId,
    currBusiness,
    onClose,
    exitFlow,
    updateTitle,
    onSkip,
  } = props;
  const config = isGSF ? configGSF : configSettings;
  const { classes } = useStyles();
  const Screen = config[state.screen].component;
  const businessNames = useSelector(businessSelectors.getBusinessNames);
  const numBusinesses = Object.keys(businessNames).length;
  const dispatch = useDispatch();
  const isNonBlank = (str) => str && str.trim() !== '';
  const bizValidation = !(
    isNonBlank(state.businessName) &&
    Boolean(state.industry) &&
    Boolean(state.color) &&
    Boolean(state.structure) &&
    !(state.businessName in businessNames) &&
    isNonBlank(state.email)
  );

  const isInvalid = state.screen === 'businessEntry' && bizValidation && !currBusiness;

  let secondaryClick;
  if (!isGSF) {
    secondaryClick = () => {
      if (currBusiness) {
        dispatch(businessActions.deleteBusiness(currBusiness));
      }
      if (!isGSF) setCurrentBusinessId(null);
      onClose();
    };
  } else if (config[state.screen].isExit) {
    secondaryClick = null;
    tracker.track(tracker.events.addBusinessComplete);
  } else if (numBusinesses === 0) {
    secondaryClick = () =>
      dispatch(
        rootUiActions.createDialog(
          rootUiTypes.mkRootUiData({
            id: 'skip-business',
            type: CONFIRMATION_DIALOG_TYPE,
            allowNesting: true,
            props: ImmutableMap(
              mkConfirmationDialogProps({
                title: 'Skip adding a business',
                content: 'Are you sure you want to skip adding a business? You can add it later from Settings.',
                confirmLabel: 'Confirm',
                denyLabel: 'Cancel',
                hideBackdrop: true,
                confirmAction: () => {
                  onSkip?.();
                  exitFlow();
                },
              }),
            ),
          }),
        ),
      );
  } else {
    secondaryClick = () => updateLocalState('screen', 'businessList');
  }

  return (
    <>
      <StdDialogContent
        id='add_business_dialog'
        className={classes.contentSpace}
        sx={{ margin: '16px', borderBottom: 'none' }}
      >
        <Dump obj={currBusiness} />
        <Screen
          state={state}
          updateLocalState={updateLocalState}
          onClose={() => {
            onClose();
            if (!isGSF) setCurrentBusinessId(null);
          }}
          business={currBusiness}
          isGSF={isGSF}
          updateTitle={updateTitle}
        />
      </StdDialogContent>
      {config[state.screen].next !== null && (
        <StdDialogActions
          primary={{
            id: 'save-btn',
            variant: 'contained',
            children:
              state.structure === 'C_CORP' ? config[state.screen].secondaryButtonText : config[state.screen].buttonText,
            onClick: () => {
              if (!config[state.screen].isExit) {
                goForward(state, currBusiness);
              } else {
                exitFlow();
              }
            },
            disabled: isInvalid,
          }}
          secondary={{
            id: 'cancel-add-biz-button',
            variant: 'text',
            children: numBusinesses > 0 ? 'Cancel' : 'skip',
            onClick: secondaryClick,
          }}
        />
      )}
    </>
  );
};

Content.propTypes = {
  goForward: PropTypes.func,
  state: PropTypes.object,
  updateLocalState: PropTypes.func,
  isGSF: PropTypes.bool,
  setCurrentBusinessId: PropTypes.func,
  currBusiness: PropTypes.object,
  onClose: PropTypes.func,
  exitFlow: PropTypes.func,
  updateTitle: PropTypes.func,
  onSkip: PropTypes.func,
};

const init = ({ isGSF, numBusinesses }) => ({
  zipCode: null,
  overtime: false,
  hideBackdrop: false,
  industry: '',
  structure: '',
  businessName: null,
  color: '',
  taxGroup: null,
  line1: null,
  line2: null,
  city: null,
  state: '',
  country: null,
  email: '',
  phoneNumber: '',
  screen: isGSF && numBusinesses > 0 ? 'businessList' : 'businessEntry',
  hasGoneForward: false,
  logoId: null,
  accountingType: 'CASH',
});

export default function AddBusiness(props) {
  const { onClose, setCurrentBusinessId, isGSF = false, exitFlow, updateTitle, onSkip } = props;

  useEffect(() => {
    tracker.track(tracker.events.addBusinessStart);
  }, []);

  const dispatch = useDispatch();
  const businessNames = useSelector(businessSelectors.getBusinessNames);
  const numBusinesses = Object.keys(businessNames).length;
  useFetchOnce(businessActions.getBusiness, businessSelectors);

  const [state, dispatchReducer] = useLazyLoadReducer(reducer, { isGSF, numBusinesses }, init, businessSelectors);

  const config = isGSF ? configGSF : configSettings;

  // if we don't have this check, we could set current business id to an existing business
  const currBusiness = state.hasGoneForward ? businessNames?.[state.businessName] : null;
  const businessIdRef = useRef(currBusiness?.id || '');

  // commenting this bc it is confusing. The reason we need to have a current business is because we are creating a business on the fly due to
  // the need to read the taxGroup from qcs. We also need to be able to go back and forth through the flow. Once created, we need to filter the business out
  // from the background of the settings page
  useEffect(() => {
    if (businessIdRef.current !== currBusiness?.id) {
      if (!isGSF) setCurrentBusinessId(currBusiness?.id);
      businessIdRef.current = currBusiness?.id;
    }
  }, [currBusiness, setCurrentBusinessId, isGSF]);

  const updateLocalState = useCallback((type, payload) => dispatchReducer({ type, payload }), [dispatchReducer]);

  const makeBusiness = useCallback(
    (stateVal, business) =>
      businessTypes.mkBusiness({
        industry: stateVal.industry,
        structure: stateVal.structure,
        name: stateVal.businessName,
        color: stateVal.color,
        clientId: uuid(),
        address: {
          line1: stateVal.line1,
          line2: stateVal.line2,
          city: stateVal.city,
          state: stateVal.state,
          country: stateVal.country,
          zipCode: stateVal.zipCode,
        },
        email: stateVal.email,
        phoneNumber: stateVal.phoneNumber,
        logoId: stateVal.logoId,
        accountingType: stateVal.accountingType,
        ...(business?.id && { id: business?.id }),
        ...(stateVal.taxGroup && { taxGroup: stateVal.taxGroup }),
      }),
    [],
  );

  const goForward = (stateVal, business) => {
    let canProceed = false;
    if (config[stateVal.screen].action === 'create' && !business) {
      updateLocalState('hasGoneForward');
      dispatch(businessActions.createBusiness(makeBusiness(stateVal).set('isDefault', numBusinesses === 0)));
      canProceed = true;
    } else if (currBusiness?.id && (config[stateVal.screen].action === 'update' || business)) {
      dispatch(businessActions.updateBusiness(makeBusiness(stateVal, business)));
      tracker.track(tracker.events.addBusinessSaved, {
        biz_name: business.name,
        industry: business.industry,
        business_structure: business.structure,
        inferred_tax_group: business.taxGroup,
      });
      canProceed = true;
    }
    if (canProceed) {
      const nextVal =
        stateVal.structure === 'C_CORP' && config[stateVal.screen].skip
          ? config[stateVal.screen].skip
          : config[stateVal.screen].next;
      if (isGSF) {
        updateTitle(config[nextVal].title);
      }
      updateLocalState('screen', nextVal);
    }
  };

  if (isGSF) {
    return (
      <Content
        goForward={goForward}
        state={state}
        updateLocalState={updateLocalState}
        isGSF={isGSF}
        setCurrentBusinessId={setCurrentBusinessId}
        currBusiness={currBusiness}
        onClose={onClose}
        exitFlow={exitFlow}
        updateTitle={updateTitle}
        onSkip={onSkip}
      />
    );
  }
  return (
    <StdDialog
      title='Tell us about your business'
      onClose={() => {
        if (currBusiness) {
          dispatch(businessActions.deleteBusiness(currBusiness));
        }
        if (!isGSF) setCurrentBusinessId(null);
        onClose();
      }}
      size='md'
      paperProps={state.hideBackdrop ? { sx: { opacity: 0.3 } } : {}}
      showBackButton={config[state.screen]?.prev !== null}
      onBack={() => {
        updateLocalState('screen', config[state.screen].prev);
      }}
      showCloseButton={state.screen !== 'successScreen'}
      disableBackdropClick={state.screen === 'successScreen'}
    >
      <Content
        goForward={goForward}
        state={state}
        updateLocalState={updateLocalState}
        isGSF={isGSF}
        setCurrentBusinessId={setCurrentBusinessId}
        currBusiness={currBusiness}
        onClose={onClose}
        updateTitle={updateTitle}
      />
    </StdDialog>
  );
}

AddBusiness.propTypes = {
  onClose: PropTypes.func,
  setCurrentBusinessId: PropTypes.func,
  isGSF: PropTypes.bool,
  exitFlow: PropTypes.func,
  updateTitle: PropTypes.func,
  onSkip: PropTypes.func,
};
